import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchUsersAction,
  deactivateEmployeeAction,
  reactivateEmployeeAction,
  User,
} from "../slices/usersSlice";
import { RootState } from "../../../../../app/rootReducer";
import { FetchUsersRequest } from "../../../../../api/fetchUsers/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { useAppDispatch } from "@/app/hooks";
import { toast } from "@/hooks/useToast";

// Helper to read initial state from URL
function getInitialStateFromUrl(searchParams: URLSearchParams) {
  const page = searchParams.get("page");
  const pageSize = searchParams.get("pageSize");
  const sortBy = searchParams.get("sortBy");
  const sortOrder = searchParams.get("sortOrder");
  const businessRoleId = searchParams.get("businessRoleId");

  return {
    pagination: {
      pageIndex: page ? parseInt(page, 10) - 1 : 0,
      pageSize: pageSize ? parseInt(pageSize, 10) : 10,
    },
    sorting: sortBy ? [{ id: sortBy, desc: sortOrder === "DESC" }] : [],
    filters: {
      firstName: searchParams.get("firstName") || undefined,
      lastName: searchParams.get("lastName") || undefined,
      email: searchParams.get("email") || undefined,
      businessRoleId: businessRoleId ? parseInt(businessRoleId, 10) : undefined,
    },
  };
}

export function useUsers() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { users, totalCount, loading, error } = useSelector(
    (state: RootState) => state.users,
  );

  // Initialize state from URL params (only on mount)
  const initialState = React.useMemo(
    () => getInitialStateFromUrl(searchParams),
    [],
  );

  const [pagination, setPagination] = useState<PaginationState>(
    initialState.pagination,
  );

  const [sorting, setSorting] = useState<SortingState>(initialState.sorting);

  const [filters, setFilters] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    businessRoleId?: number;
  }>(initialState.filters);

  const requestData = React.useMemo<FetchUsersRequest>(() => {
    return {
      page: pagination.pageIndex + 1,
      sortBy: sorting.length > 0 ? sorting[0].id : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
      firstName: filters.firstName,
      lastName: filters.lastName,
      email: filters.email,
      businessRoleId: filters.businessRoleId,
      pageSize: pagination.pageSize,
    };
  }, [pagination, sorting, filters]);

  useEffect(() => {
    dispatch(fetchUsersAction(requestData));
  }, [dispatch, requestData]);

  const refetch = React.useCallback(() => {
    dispatch(fetchUsersAction(requestData));
  }, [dispatch, requestData]);

  // Deactivate / reactivate (soft-delete) — company-scoped on the backend.
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const openDeactivateUser = React.useCallback(
    (user: User) => setUserToDeactivate(user),
    [],
  );
  const closeDeactivateUser = React.useCallback(
    () => setUserToDeactivate(null),
    [],
  );
  const openReactivateUser = React.useCallback(
    (user: User) => setUserToReactivate(user),
    [],
  );
  const closeReactivateUser = React.useCallback(
    () => setUserToReactivate(null),
    [],
  );

  const confirmDeactivateUser = React.useCallback(async () => {
    if (!userToDeactivate?.employeeUuid) {
      return;
    }

    setIsDeactivating(true);
    try {
      await dispatch(deactivateEmployeeAction(userToDeactivate.employeeUuid));
      toast({
        title: "User Deactivated",
        description: `${userToDeactivate.firstName} ${userToDeactivate.lastName} no longer has access.`,
      });
      setUserToDeactivate(null);
    } finally {
      setIsDeactivating(false);
    }
  }, [dispatch, userToDeactivate]);

  const confirmReactivateUser = React.useCallback(async () => {
    if (!userToReactivate?.employeeUuid) {
      return;
    }

    setIsReactivating(true);
    try {
      await dispatch(reactivateEmployeeAction(userToReactivate.employeeUuid));
      toast({
        title: "User Reactivated",
        description: `${userToReactivate.firstName} ${userToReactivate.lastName} has regained access.`,
      });
      setUserToReactivate(null);
    } finally {
      setIsReactivating(false);
    }
  }, [dispatch, userToReactivate]);

  // Sync state to URL whenever it changes
  useEffect(() => {
    const params = new URLSearchParams();

    // Pagination
    if (pagination.pageIndex > 0) {
      params.set("page", (pagination.pageIndex + 1).toString());
    }
    if (pagination.pageSize !== 10) {
      params.set("pageSize", pagination.pageSize.toString());
    }

    // Sorting
    if (sorting.length > 0) {
      params.set("sortBy", sorting[0].id);
      params.set("sortOrder", sorting[0].desc ? "DESC" : "ASC");
    }

    // Filters
    if (filters.firstName) params.set("firstName", filters.firstName);
    if (filters.lastName) params.set("lastName", filters.lastName);
    if (filters.email) params.set("email", filters.email);
    if (filters.businessRoleId !== undefined) {
      params.set("businessRoleId", filters.businessRoleId.toString());
    }

    setSearchParams(params, { replace: true });
  }, [pagination, sorting, filters, setSearchParams]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (
    filterKey: string,
    value: string | number | undefined,
  ) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: value,
    }));
  };

  return {
    users,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    refetch,
    userToDeactivate,
    isDeactivating,
    userToReactivate,
    isReactivating,
    openDeactivateUser,
    closeDeactivateUser,
    openReactivateUser,
    closeReactivateUser,
    confirmDeactivateUser,
    confirmReactivateUser,
  };
}
